// YouTube Data API service for searching videos, channels, and playlists
import { supabase } from '@/integrations/supabase/client';

interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  channelTitle: string;
  publishedAt: string;
  viewCount?: string;
  duration?: string;
  url: string;
}

interface YouTubeChannel {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  subscriberCount?: string;
  videoCount?: string;
  url: string;
}

interface YouTubeSearchResult {
  videos: YouTubeVideo[];
  channels: YouTubeChannel[];
  totalResults: number;
  query: string;
}

export const youtubeService = async (
  query: string,
  type: "video" | "channel" | "all" = "all",
  maxResults: number = 10,
  order: "relevance" | "date" | "rating" | "viewCount" | "title" = "relevance"
): Promise<YouTubeSearchResult> => {
  try {
    const { data, error } = await supabase.functions.invoke('runAgent', {
      body: { agent: 'youtube', query, type, maxResults, order }
    });

    if (error) {
      throw new Error(error.message);
    }

    if (data && data.error) {
      throw new Error(data.error);
    }

    // Format stats for display since backend returns raw stats
    const formattedVideos = (data.videos || []).map((video: any) => ({
      ...video,
      viewCount: video.viewCount ? formatViewCount(video.viewCount) : undefined,
      duration: video.duration ? formatDuration(video.duration) : undefined,
    }));

    const formattedChannels = (data.channels || []).map((channel: any) => ({
      ...channel,
      subscriberCount: channel.subscriberCount ? formatSubscriberCount(channel.subscriberCount) : undefined,
    }));

    return {
      videos: formattedVideos,
      channels: formattedChannels,
      totalResults: data.totalResults || 0,
      query: data.query || query
    };
  } catch (error: any) {
    console.error("YouTube API Error:", error);
    throw new Error(error.message || "Failed to search YouTube. Please try again.");
  }
};

// Helper functions
const formatViewCount = (count: string): string => {
  const num = parseInt(count);
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M views`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K views`;
  }
  return `${num} views`;
};

const formatSubscriberCount = (count: string): string => {
  const num = parseInt(count);
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M subscribers`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K subscribers`;
  }
  return `${num} subscribers`;
};

const formatDuration = (duration: string): string => {
  const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  if (!match) return "";

  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  const seconds = match[3] ? parseInt(match[3]) : 0;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const formatYouTubeResponse = (
  data: YouTubeSearchResult,
  query: string,
  isDarkMode?: boolean
): string => {
  const { videos, channels } = data;

  if (videos.length === 0 && channels.length === 0) {
    return `
<div data-isdarkmode="${isDarkMode ? 'true' : 'false'}" class="rounded-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border p-6 text-center my-4">
  <h2 class="text-xl font-semibold mb-2">📺 No YouTube Results Found</h2>
  <p class="text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}">No videos or channels found for "${query}". Try different keywords!</p>
</div>`;
  }

  let response = `
<div data-isdarkmode="${isDarkMode ? 'true' : 'false'}" class="space-y-6">`;

  // Videos section
  if (videos.length > 0) {
    response += `
  <div class="space-y-3">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
      ${videos
        .map((video, index) => `
        <div data-isdarkmode="${isDarkMode ? 'true' : 'false'}" class="group rounded-xl overflow-hidden border ${
          isDarkMode ? 'bg-[#121212] border-gray-700' : 'bg-white border-gray-200'
        }">
          <div class="relative bg-black">
            <iframe
              id="video-player-${index}"
              src="https://www.youtube.com/embed/${video.id}?enablejsapi=1&controls=1&modestbranding=1&rel=0"
              class="w-full h-[200px] border-0 block"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            ${
              video.duration
                ? `<span class="absolute bottom-2 right-2 text-[11px] font-semibold text-white bg-black/60 px-1.5 py-0.5 rounded-md">${video.duration}</span>`
                : ''
            }
          </div>
          <div class="p-3">
            <h4 class="text-[14px] font-semibold leading-tight line-clamp-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }">${video.title}</h4>
            <p class="text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}">${video.channelTitle}</p>
            <div class="flex items-center justify-between text-[11px] mt-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }">
              <span>${video.viewCount || 'Unknown views'}</span>
              <span>${new Date(video.publishedAt).toLocaleDateString()}</span>
            </div>
            <div class="mt-3 flex justify-end">
              <button
                type="button"
                class="text-xs px-2 py-1 rounded-md border ${isDarkMode ? 'border-gray-700 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-100'}"
                data-summarize-url="https://www.youtube.com/watch?v=${video.id}"
              >📝 Summarize</button>
            </div>
          </div>
        </div>
      `)
        .join('')}
    </div>
  </div>`;
  }

  // Channels section
  if (channels.length > 0) {
    response += `
  <div class="space-y-3">
    <h3 class="text-base font-semibold flex items-center gap-2 ${
      isDarkMode ? 'text-white' : 'text-gray-900'
    }">👤 Channels (${channels.length})</h3>
    <div data-isdarkmode="${isDarkMode ? 'true' : 'false'}" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
      ${channels
        .map(
          (channel) => `
        <div data-isdarkmode="${isDarkMode ? 'true' : 'false'}" class="rounded-xl p-4 border ${
          isDarkMode ? 'bg-[#121212] border-gray-700' : 'bg-white border-gray-200'
        }">
          <div class="flex items-center gap-3 mb-3">
            <img src="${channel.thumbnail}" alt="${channel.title}" class="w-12 h-12 rounded-full object-cover" />
            <div class="min-w-0">
              <h4 class="text-sm font-semibold truncate ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }">${channel.title}</h4>
              <p class="text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}">${
                channel.subscriberCount || 'Unknown subscribers'
              }</p>
            </div>
          </div>
          <p class="text-[13px] leading-snug line-clamp-2 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }">${channel.description || 'No description available'}</p>
          <a
            href="${channel.url}"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-block mt-3 text-xs font-medium px-3 py-1.5 rounded-md decoration-none transition-colors ${
              isDarkMode
                ? 'text-white border border-gray-700 hover:bg-gray-800'
                : 'text-gray-800 border border-gray-300 hover:bg-gray-100'
            }"
          >Visit Channel</a>
        </div>
      `
        )
        .join('')}
    </div>
  </div>`;
  }

  response += `</div>`;

  return response;
};
